import React from 'react';
import { Badge, Button, Col, Container, Form, Row, Spinner, Tab, Tabs } from 'react-bootstrap';
import { MsalAuthenticationTemplate, useMsal } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faList, faPlay, faRefresh } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { loginRequest } from '../../authConfig';
import { PageLayout2 } from '../../Components/PageLayout/PageLayout2';
import ExecutionRunList from '../../Components/ExecutionRuns/ExecutionRuns';
import InformationModal from '../../Components/InformationModal/InformationModal';
import ConfirmationModal from '../../Components/ConfirmationModal/ConfirmationModal';
import { ConciliationExtractionsApi } from '../../Services/ConciliationApiDataService';
import ConcurExpenseProcessingPanel from './ConcurExpenseProcessingPanel';
import './ConciliationExtractionsPage.scss';

const todayISO = () => new Date().toISOString().slice(0, 10);
const fallbackPeriods = ['yesterday', 'lastWeek', 'lastMonth', 'lastYear'];

const valueOf = (obj, keys, fallback = null) => {
  for (const key of keys) {
    if (obj && obj[key] !== undefined && obj[key] !== null) return obj[key];
  }
  return fallback;
};

const titleCaseKeyword = (value) =>
  String(value || '')
    .replace(/([A-Z])/g, ' $1')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();

const normalizeMessages = (messages) => {
  if (!messages) return [];
  if (Array.isArray(messages)) return messages;
  return [String(messages)];
};

const getErrorMessage = (err, fallback) =>
  err?.response?.data?.message || err?.response?.data?.Message || err?.message || fallback;

const buildPeriodPayload = (periodState) => {
  const isCustom = periodState.periodKeyword === 'custom';
  return {
    periodKeyword: isCustom ? null : periodState.periodKeyword || null,
    fromDate: isCustom ? (periodState.fromDate || null) : null,
    toDate: isCustom ? (periodState.toDate || null) : null
  };
};

const formatSelectedPeriod = (period) =>
  period.periodKeyword === 'custom'
    ? `${period.fromDate || '-'} to ${period.toDate || '-'}`
    : titleCaseKeyword(period.periodKeyword);

const PeriodSelector = ({ value, onChange, periodKeywords }) => {
  const periods = periodKeywords?.length ? periodKeywords : fallbackPeriods;
  const selectPeriod = (periodKeyword) => {
    onChange({
      periodKeyword,
      fromDate: periodKeyword === 'custom' ? value.fromDate : '',
      toDate: periodKeyword === 'custom' ? value.toDate : ''
    });
  };

  return (
    <div>
      <Form.Label className="fw-bold mb-2">Period</Form.Label>
      <div className="period-options">
        {periods.map((period) => (
          <Badge
            key={period}
            bg={value.periodKeyword === period ? 'primary' : 'light'}
            text={value.periodKeyword === period ? undefined : 'dark'}
            onClick={() => selectPeriod(period)}
          >
            {titleCaseKeyword(period)}
          </Badge>
        ))}
        <Badge
          bg={value.periodKeyword === 'custom' ? 'primary' : 'light'}
          text={value.periodKeyword === 'custom' ? undefined : 'dark'}
          onClick={() => selectPeriod('custom')}
        >
          Custom
        </Badge>
      </div>
      {value.periodKeyword === 'custom' && (
        <Row className="g-2 mt-2">
          <Col>
            <Form.Label className="small text-muted mb-1">From</Form.Label>
            <Form.Control
              size="sm"
              type="date"
              value={value.fromDate}
              max={todayISO()}
              onChange={(e) => onChange({ ...value, fromDate: e.target.value })}
            />
          </Col>
          <Col>
            <Form.Label className="small text-muted mb-1">To</Form.Label>
            <Form.Control
              size="sm"
              type="date"
              value={value.toDate}
              max={todayISO()}
              onChange={(e) => onChange({ ...value, toDate: e.target.value })}
            />
          </Col>
        </Row>
      )}
    </div>
  );
};

const SapTargets = ({ companies, selectedIds, setSelectedIds }) => {
  const [search, setSearch] = React.useState('');
  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter((c) =>
      `${c.companyName || ''} ${c.companyDb || ''}`.toLowerCase().includes(q)
    );
  }, [companies, search]);

  const allSelected = selectedIds === null
    || (companies.length > 0 && companies.every((company) => selectedIds.includes(company.companyId)));
  const toggleCompany = (id) => {
    setSelectedIds((prev) => (
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    ));
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <Form.Label className="fw-bold mb-0">Companies</Form.Label>
        <Button
          size="sm"
          variant={allSelected ? 'primary' : 'outline-primary'}
          onClick={() => setSelectedIds(allSelected ? [] : null)}
        >
          All companies
        </Button>
      </div>
      <Form.Control
        size="sm"
        className="mb-2"
        placeholder="Search company or DB..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div className="target-list">
        {filtered.map((company) => {
          const id = company.companyId;
          return (
            <label className="target-row" key={id}>
              <Form.Check
                type="checkbox"
                checked={allSelected || selectedIds.includes(id)}
                onChange={() => {
                  if (allSelected) setSelectedIds(companies.map((c) => c.companyId).filter((companyId) => companyId !== id));
                  else toggleCompany(id);
                }}
              />
              <span>
                <span className="target-title">{company.companyName || `Company ${id}`}</span>
                <span className="target-subtitle d-block">
                  {company.companyDb || 'No DB'} {company.hasSapAuth === false ? '| SAP auth missing' : ''}
                </span>
              </span>
            </label>
          );
        })}
        {filtered.length === 0 && <div className="p-3 text-muted small">No companies match the search.</div>}
      </div>
    </div>
  );
};

const BankTargets = ({ bankCompanies, selectedIds, setSelectedIds }) => {
  const [search, setSearch] = React.useState('');
  const accounts = React.useMemo(
    () => bankCompanies.flatMap((company) => (company.accounts || []).map((account) => ({ ...account, groupCompany: company }))),
    [bankCompanies]
  );
  const filteredCompanies = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return bankCompanies
      .map((company) => ({
        ...company,
        accounts: (company.accounts || []).filter((account) => {
          if (!q) return true;
          return `${company.companyName || ''} ${account.accountNumber || ''} ${account.accountCurrency || ''} ${account.bankName || ''}`
            .toLowerCase()
            .includes(q);
        })
      }))
      .filter((company) => company.accounts.length > 0);
  }, [bankCompanies, search]);

  const allSelected = selectedIds === null
    || (accounts.length > 0 && accounts.every((account) => selectedIds.includes(account.companyBankAccountId)));
  const toggleAccount = (id) => {
    setSelectedIds((prev) => (
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    ));
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <Form.Label className="fw-bold mb-0">Accounts</Form.Label>
        <Button
          size="sm"
          variant={allSelected ? 'primary' : 'outline-primary'}
          onClick={() => setSelectedIds(allSelected ? [] : null)}
        >
          All accounts
        </Button>
      </div>
      <Form.Control
        size="sm"
        className="mb-2"
        placeholder="Search company, account, currency or bank..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div className="target-list">
        {filteredCompanies.map((company) => (
          <div key={company.companyId}>
            <div className="target-group-header">
              {company.companyName || `Company ${company.companyId}`}
              {company.taxId ? <span className="text-muted fw-normal">| {company.taxId}</span> : null}
            </div>
            {company.accounts.map((account) => {
              const id = account.companyBankAccountId;
              return (
                <label className="target-row" key={id}>
                  <Form.Check
                    type="checkbox"
                    checked={allSelected || selectedIds.includes(id)}
                    onChange={() => {
                      if (allSelected) setSelectedIds(accounts.map((a) => a.companyBankAccountId).filter((accountId) => accountId !== id));
                      else toggleAccount(id);
                    }}
                  />
                  <span>
                    <span className="target-title">{account.accountNumber || `Account ${id}`}</span>
                    <span className="target-subtitle d-block">
                      {account.accountCurrency || '-'} | {account.bankName || 'No bank'} | {account.serviceName || 'No service'}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        ))}
        {filteredCompanies.length === 0 && <div className="p-3 text-muted small">No accounts match the search.</div>}
      </div>
    </div>
  );
};

const RunSummary = ({ result, placeholder = 'Results will appear here after the extraction finishes.' }) => {
  if (!result) return <div className="run-summary text-muted small">{placeholder}</div>;

  const requestedTargets = valueOf(result, ['requestedTargets', 'RequestedTargets'], 0);
  const success = valueOf(result, ['success', 'Success'], 0);
  const failed = valueOf(result, ['failed', 'Failed'], 0);
  const fromDate = valueOf(result, ['fromDate', 'FromDate'], null);
  const toDate = valueOf(result, ['toDate', 'ToDate'], null);
  const messages = normalizeMessages(valueOf(result, ['messages', 'Messages'], []));

  return (
    <div className="run-summary">
      <div className="summary-grid mb-2">
        <div className="summary-stat"><span className="label">Requested</span><span className="value">{requestedTargets}</span></div>
        <div className="summary-stat"><span className="label">Success</span><span className="value text-success">{success}</span></div>
        <div className="summary-stat"><span className="label">Failed</span><span className="value text-danger">{failed}</span></div>
      </div>
      {(fromDate || toDate) && <div className="small text-muted mb-2">{fromDate || '-'} to {toDate || '-'}</div>}
      {messages.length > 0 && (
        <ul className="small mb-0 ps-3">
          {messages.slice(0, 4).map((message, index) => <li key={`${message}-${index}`}>{message}</li>)}
        </ul>
      )}
    </div>
  );
};

const ExtractionPanel = ({
  type,
  description,
  period,
  setPeriod,
  periodKeywords,
  targetCount,
  children,
  running,
  onRunRequest,
  result,
  showTargetCount = true,
  canRun = targetCount > 0,
  actionLabel = 'Run extraction',
  runningLabel = 'Running extraction...',
  summaryLabel = 'What will be extracted',
  resultPlaceholder
}) => (
  <div className="extraction-panel">
    {period && (
      <div className="panel-row">
        <PeriodSelector value={period} onChange={setPeriod} periodKeywords={periodKeywords} />
      </div>
    )}
    {children && <div className="panel-row">{children}</div>}
    <div className="panel-row run-row">
      <div className="run-summary">
        <div className="small text-muted">{summaryLabel}</div>
        <div className="fw-bold">{type}</div>
        {description && <div className="small text-muted mt-1">{description}</div>}
        <div className="d-flex flex-wrap gap-3 mt-2 small">
          {period && <span><span className="text-muted">Period:</span> {formatSelectedPeriod(period)}</span>}
          {showTargetCount && <span><span className="text-muted">Targets:</span> <strong>{targetCount}</strong></span>}
        </div>
      </div>
      <div className="run-action">
        <Button className="w-100 fw-bold" variant="primary" disabled={running || !canRun} onClick={onRunRequest}>
          {running ? <Spinner animation="border" size="sm" className="me-2" /> : <FontAwesomeIcon icon={faPlay} className="me-2" />}
          {running ? runningLabel : actionLabel}
        </Button>
        <RunSummary result={result} placeholder={resultPlaceholder} />
      </div>
    </div>
  </div>
);

const RecentRunsPanel = ({ recentRuns, loading, onRefresh, onOpenLogs, onOpenSchedules }) => {
  return (
    <div className="recent-runs-panel">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div>
          <h6 className="fw-bold mb-0">Recent runs</h6>
          <span className="text-muted small">{recentRuns.length} shown</span>
        </div>
        <Button variant="outline-primary" size="sm" disabled={loading} onClick={onRefresh} title="Refresh overview">
          {loading ? <Spinner animation="border" size="sm" /> : <FontAwesomeIcon icon={faRefresh} />}
        </Button>
      </div>
      <div className="recent-runs-list">
        <ExecutionRunList executions={recentRuns} compact emptyMessage="No recent executions." />
      </div>
      <div className="extended-actions">
        <Button variant="outline-secondary" size="sm" onClick={onOpenLogs}>
          <FontAwesomeIcon icon={faList} className="me-2" />
          Execution logs
        </Button>
        <Button variant="outline-secondary" size="sm" onClick={onOpenSchedules}>
          <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
          Scheduled jobs
        </Button>
      </div>
    </div>
  );
};

export const ConciliationExtractionsPageContent = () => {
  const { instance, accounts, inProgress } = useMsal();
  const navigate = useNavigate();
  const [overview, setOverview] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState('sap');
  const [sapPeriod, setSapPeriod] = React.useState({ periodKeyword: 'yesterday', fromDate: '', toDate: '' });
  const [bankPeriod, setBankPeriod] = React.useState({ periodKeyword: 'yesterday', fromDate: '', toDate: '' });
  const [sapCompanyIds, setSapCompanyIds] = React.useState(null);
  const [sapCatalogCompanyIds, setSapCatalogCompanyIds] = React.useState(null);
  const [bankAccountIds, setBankAccountIds] = React.useState(null);
  const [running, setRunning] = React.useState(null);
  const [sapResult, setSapResult] = React.useState(null);
  const [sapCatalogResult, setSapCatalogResult] = React.useState(null);
  const [bankResult, setBankResult] = React.useState(null);
  const [concurSyncResult, setConcurSyncResult] = React.useState(null);
  const [infoModal, setInfoModal] = React.useState({ show: false, title: '', content: null });
  const [confirmRun, setConfirmRun] = React.useState(null);

  const acquireToken = React.useCallback(async () => {
    const req = { ...loginRequest, account: accounts[0] };
    const { accessToken } = await instance.acquireTokenSilent(req);
    return accessToken;
  }, [accounts, instance]);

  const loadOverview = React.useCallback(async () => {
    if (inProgress !== 'none' || !accounts.length) return;
    try {
      setLoading(true);
      const token = await acquireToken();
      const data = await ConciliationExtractionsApi.getOverview(token);
      setOverview(data || {});
      const keywords = data?.periodKeywords || [];
      const defaultKeyword = keywords.includes('yesterday') ? 'yesterday' : (keywords[0] || 'yesterday');
      setSapPeriod((prev) => ({ ...prev, periodKeyword: prev.periodKeyword || defaultKeyword }));
      setBankPeriod((prev) => ({ ...prev, periodKeyword: prev.periodKeyword || defaultKeyword }));
    } catch (err) {
      console.error('Failed to load conciliation extractions overview', err);
      setInfoModal({ show: true, title: 'Load failed', content: <p className="mb-0">{getErrorMessage(err, 'Could not load the overview.')}</p> });
    } finally {
      setLoading(false);
    }
  }, [accounts.length, acquireToken, inProgress]);

  React.useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const sapCompanies = React.useMemo(() => overview?.sapCompanies || [], [overview?.sapCompanies]);
  const bankCompanies = React.useMemo(() => overview?.bankCompanies || [], [overview?.bankCompanies]);
  const bankAccounts = React.useMemo(() => bankCompanies.flatMap((company) => company.accounts || []), [bankCompanies]);
  const periodKeywords = overview?.periodKeywords || fallbackPeriods;

  const sapTargetCount = sapCompanyIds === null ? sapCompanies.length : sapCompanyIds.length;
  const sapCatalogTargetCount = sapCatalogCompanyIds === null ? sapCompanies.length : sapCatalogCompanyIds.length;
  const bankTargetCount = bankAccountIds === null ? bankAccounts.length : bankAccountIds.length;

  const selectedSapTargets = React.useMemo(() => {
    if (sapCompanyIds === null) return sapCompanies.map((company) => company.companyName || `Company ${company.companyId}`);
    return sapCompanies
      .filter((company) => sapCompanyIds.includes(company.companyId))
      .map((company) => `${company.companyName || `Company ${company.companyId}`} (${company.companyDb || 'No DB'})`);
  }, [sapCompanies, sapCompanyIds]);

  const selectedSapCatalogTargets = React.useMemo(() => {
    if (sapCatalogCompanyIds === null) return sapCompanies.map((company) => company.companyName || `Company ${company.companyId}`);
    return sapCompanies
      .filter((company) => sapCatalogCompanyIds.includes(company.companyId))
      .map((company) => `${company.companyName || `Company ${company.companyId}`} (${company.companyDb || 'No DB'})`);
  }, [sapCompanies, sapCatalogCompanyIds]);

  const selectedBankTargets = React.useMemo(() => {
    if (bankAccountIds === null) {
      return bankAccounts.map((account) => `${account.companyName || 'Company'} - ${account.accountNumber || `Account ${account.companyBankAccountId}`}`);
    }
    return bankAccounts
      .filter((account) => bankAccountIds.includes(account.companyBankAccountId))
      .map((account) => `${account.companyName || 'Company'} - ${account.accountNumber || `Account ${account.companyBankAccountId}`} (${account.accountCurrency || '-'})`);
  }, [bankAccounts, bankAccountIds]);

  const openRunConfirmation = (kind) => {
    const isSap = kind === 'sap';
    const isSapCatalogs = kind === 'sap-catalogs';
    const isConcurSync = kind === 'concur-sync';
    setConfirmRun({
      kind,
      title: isSap ? 'SAP Extraction' : (isSapCatalogs ? 'SAP Catalogs' : (isConcurSync ? 'Concur Sync' : 'Bank Statements')),
      type: isSap ? 'SAP company data' : (isSapCatalogs ? 'SAP master data/catalogs only' : (isConcurSync ? 'All open Concur reports and related data' : 'CAMT053 bank statements')),
      period: isSap ? sapPeriod : (isSapCatalogs || isConcurSync ? null : bankPeriod),
      targetCount: isSap ? sapTargetCount : (isSapCatalogs ? sapCatalogTargetCount : (isConcurSync ? null : bankTargetCount)),
      showTargets: !isConcurSync,
      allTargetsLabel: isSap || isSapCatalogs ? 'All companies' : 'All accounts',
      targetLabels: isSap ? selectedSapTargets : (isSapCatalogs ? selectedSapCatalogTargets : (isConcurSync ? [] : selectedBankTargets))
    });
  };

  const confirmRunExtraction = async () => {
    const kind = confirmRun?.kind;
    setConfirmRun(null);
    if (kind === 'sap') await runSap();
    if (kind === 'sap-catalogs') await runSapCatalogs();
    if (kind === 'bank') await runBank();
    if (kind === 'concur-sync') await runConcurSynchronization();
  };

  const runSap = async () => {
    try {
      setRunning('sap');
      const token = await acquireToken();
      const result = await ConciliationExtractionsApi.runSapExtraction({
        ...buildPeriodPayload(sapPeriod),
        companyIds: sapCompanyIds
      }, token);
      setSapResult(result);
      await loadOverview();
    } catch (err) {
      console.error('SAP extraction failed', err);
      setInfoModal({ show: true, title: 'SAP extraction failed', content: <p className="mb-0">{getErrorMessage(err, 'Could not run SAP extraction.')}</p> });
    } finally {
      setRunning(null);
    }
  };

  const runBank = async () => {
    try {
      setRunning('bank');
      const token = await acquireToken();
      const result = await ConciliationExtractionsApi.runBankStatementsExtraction({
        ...buildPeriodPayload(bankPeriod),
        companyBankAccountIds: bankAccountIds
      }, token);
      setBankResult(result);
      await loadOverview();
    } catch (err) {
      console.error('Bank statements extraction failed', err);
      setInfoModal({ show: true, title: 'Bank statements extraction failed', content: <p className="mb-0">{getErrorMessage(err, 'Could not run bank statements extraction.')}</p> });
    } finally {
      setRunning(null);
    }
  };

  const runSapCatalogs = async () => {
    try {
      setRunning('sap-catalogs');
      const token = await acquireToken();
      const result = await ConciliationExtractionsApi.runSapCatalogsExtraction({
        companyIds: sapCatalogCompanyIds
      }, token);
      setSapCatalogResult(result);
      await loadOverview();
    } catch (err) {
      console.error('SAP catalogs extraction failed', err);
      setInfoModal({ show: true, title: 'SAP catalogs extraction failed', content: <p className="mb-0">{getErrorMessage(err, 'Could not run SAP catalogs extraction.')}</p> });
    } finally {
      setRunning(null);
    }
  };

  const runConcurSynchronization = async () => {
    try {
      setRunning('concur-sync');
      const token = await acquireToken();
      const response = await ConciliationExtractionsApi.runConcurSync(token);
      const message = valueOf(response, ['message', 'Message'], 'All open reports and related Concur data were synchronized successfully.');
      setConcurSyncResult({ requestedTargets: 1, success: 1, failed: 0, messages: [message] });
      setInfoModal({
        show: true,
        title: 'Concur sync completed',
        content: <p className="mb-0">{message}</p>
      });
      await loadOverview();
    } catch (err) {
      console.error('Concur synchronization failed', err);
      setInfoModal({
        show: true,
        title: 'Concur sync failed',
        content: <p className="mb-0">{getErrorMessage(err, 'Could not synchronize Concur data.')}</p>
      });
    } finally {
      setRunning(null);
    }
  };

  const wideTab = activeTab === 'concur';

  return (
    <Container fluid className="h-100 d-flex flex-column conciliation-extractions-page">
      <Row className="g-3 flex-grow-1">
        <Col xl={wideTab ? 12 : 7} xxl={wideTab ? 12 : 8}>
          <Tabs activeKey={activeTab} onSelect={(key) => setActiveTab(key || 'sap')} className="mb-0 manual-posting-tabs">
            <Tab eventKey="sap" title="SAP Extraction">
              <ExtractionPanel
                type="SAP company data"
                description="Extracts SAP transactional documents and related company data for the selected period."
                period={sapPeriod}
                setPeriod={setSapPeriod}
                periodKeywords={periodKeywords}
                targetCount={sapTargetCount}
                running={running === 'sap'}
                onRunRequest={() => openRunConfirmation('sap')}
                result={sapResult}
              >
                <SapTargets companies={sapCompanies} selectedIds={sapCompanyIds} setSelectedIds={setSapCompanyIds} />
              </ExtractionPanel>
            </Tab>
            <Tab eventKey="sap-catalogs" title="SAP Catalogs">
              <ExtractionPanel
                type="SAP master data/catalogs"
                description="Extracts SAP master data and catalogs only. Transactional documents are not extracted."
                targetCount={sapCatalogTargetCount}
                running={running === 'sap-catalogs'}
                onRunRequest={() => openRunConfirmation('sap-catalogs')}
                result={sapCatalogResult}
              >
                <SapTargets companies={sapCompanies} selectedIds={sapCatalogCompanyIds} setSelectedIds={setSapCatalogCompanyIds} />
              </ExtractionPanel>
            </Tab>
            <Tab eventKey="bank" title="BAC Bank Statements">
              <ExtractionPanel
                type="CAMT053 bank statements"
                description="Extracts bank statements from BAC API for the selected period."
                period={bankPeriod}
                setPeriod={setBankPeriod}
                periodKeywords={periodKeywords}
                targetCount={bankTargetCount}
                running={running === 'bank'}
                onRunRequest={() => openRunConfirmation('bank')}
                result={bankResult}
              >
                <BankTargets bankCompanies={bankCompanies} selectedIds={bankAccountIds} setSelectedIds={setBankAccountIds} />
              </ExtractionPanel>
            </Tab>
            <Tab eventKey="concur-sync" title="Concur Sync">
              <ExtractionPanel
                type="Concur synchronization"
                description="Synchronizes all open reports and related Concur data from the configured Concur environment. This sync always uses the configured integration company (CompanyId 1)."
                showTargetCount={false}
                canRun
                running={running === 'concur-sync'}
                onRunRequest={() => openRunConfirmation('concur-sync')}
                result={concurSyncResult}
                actionLabel="Synchronize Concur"
                runningLabel="Synchronizing Concur..."
                summaryLabel="What will be synchronized"
                resultPlaceholder="Sync results will appear here after the request finishes."
              />
            </Tab>
            <Tab eventKey="concur" title="Concur Expense Reports" mountOnEnter>
              <ConcurExpenseProcessingPanel
                companies={sapCompanies}
                acquireToken={acquireToken}
                showInfoModal={(title, content) => setInfoModal({ show: true, title, content })}
              />
            </Tab>
          </Tabs>
        </Col>
        {!wideTab && (
          <Col xl={5} xxl={4}>
            <RecentRunsPanel
              recentRuns={overview?.recentRuns || []}
              loading={loading}
              onRefresh={loadOverview}
              onOpenLogs={() => navigate('/execution-logs')}
              onOpenSchedules={() => navigate('/scheduled-tasks')}
            />
          </Col>
        )}
      </Row>

      <ConfirmationModal
        show={!!confirmRun}
        onHide={() => setConfirmRun(null)}
        onCancelClick={() => setConfirmRun(null)}
        onAcceptClick={confirmRunExtraction}
        title={`Confirm ${confirmRun?.title || 'extraction'}`}
        content={
          confirmRun && (
            <div>
              <div className="summary-grid mb-3">
                <div className="summary-stat"><span className="label">Extraction</span><span className="value">{confirmRun.type}</span></div>
                {confirmRun.period && <div className="summary-stat"><span className="label">Period</span><span className="value">{formatSelectedPeriod(confirmRun.period)}</span></div>}
                {confirmRun.showTargets && <div className="summary-stat"><span className="label">Targets</span><span className="value">{confirmRun.targetCount}</span></div>}
              </div>
              {confirmRun.showTargets ? (
                <>
                  <div className="small text-muted mb-2">
                    {confirmRun.targetLabels.length === confirmRun.targetCount && confirmRun.targetCount > 0
                      ? 'The extraction will run for these targets:'
                      : `The extraction will run for ${confirmRun.allTargetsLabel.toLowerCase()}.`}
                  </div>
                  <div className="confirm-target-list">
                    {(confirmRun.targetLabels.length ? confirmRun.targetLabels : [confirmRun.allTargetsLabel]).slice(0, 12).map((label, index) => (
                      <div key={`${label}-${index}`}>{label}</div>
                    ))}
                    {confirmRun.targetLabels.length > 12 && (
                      <div className="text-muted">+{confirmRun.targetLabels.length - 12} more</div>
                    )}
                  </div>
                </>
              ) : (
                <p className="mb-0 small text-muted">
                  This action will synchronize the configured Concur environment without selecting a company or account.
                </p>
              )}
            </div>
          )
        }
      />

      <InformationModal
        show={infoModal.show}
        onHide={() => setInfoModal((prev) => ({ ...prev, show: false }))}
        onAcceptClick={() => setInfoModal((prev) => ({ ...prev, show: false }))}
        title={infoModal.title}
        content={infoModal.content}
      />
    </Container>
  );
};

const ConciliationExtractionsPage = () => (
  <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={loginRequest}>
    <PageLayout2 title="Conciliation Extractions">
      <ConciliationExtractionsPageContent />
    </PageLayout2>
  </MsalAuthenticationTemplate>
);

export default ConciliationExtractionsPage;
